import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Accordion from 'components/Accordion';
import DatePicker from 'components/DatePicker';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import useAnalysisPeriod from 'hooks/Meteorology/Fields/ModelPerformance/useAnalysisPeriod';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import { ResponseError } from 'services/api/base';
import { useListarDatasValidadePrevisaoQuery } from 'services/api/Meteorology/Fields/ModelPerformance';
import {
  selectMeteorologyFieldsModelPerformanceAnalysisType,
  selectMeteorologyFieldsModelPerformanceVariable,
  selectMeteorologyFieldsModelPerformanceSelectedRounds,
} from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import { parse } from 'date-fns';

export default function AnalysisPeriod() {
  const { analysisPeriod, setAnalysisPeriod } = useAnalysisPeriod();
  const { periodType } = usePeriodType();
  const analysisType = useSelector(selectMeteorologyFieldsModelPerformanceAnalysisType);
  const variable = useSelector(selectMeteorologyFieldsModelPerformanceVariable);
  const selectedRoundsStrings = useSelector(selectMeteorologyFieldsModelPerformanceSelectedRounds);

  const {
    data: validityDates = [],
    isError: validityError,
    error: validityErrorPayload,
    isLoading: validityLoading,
    isSuccess: validitySuccess,
  } = useListarDatasValidadePrevisaoQuery(
    {
      tipo_analise: analysisType ?? '',
      variavel: variable ?? '',
      datas_rodada: selectedRoundsStrings ?? [],
    },
    {
      skip: periodType !== 'free'
        || !selectedRoundsStrings?.length
        || !analysisType
        || !variable,
    },
  );

  const defaultValidityMessage = 'Nenhuma data de validade comum encontrada para as rodadas informadas.';
  let validityErrorMessage: string | null = null;
  if (validityError) {
    validityErrorMessage = (validityErrorPayload as ResponseError)?.description
      || (validityErrorPayload as ResponseError)?.data
      || defaultValidityMessage;
  } else if (validitySuccess && !validityLoading && validityDates.length === 0) {
    validityErrorMessage = defaultValidityMessage;
  }

  const [localStartDate, setLocalStartDate] = useState<Date | null>(null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null);

  const hasSelectedRounds = Boolean(selectedRoundsStrings?.length);

  // Limpar estado local quando Limpar filtros é clicado (periodType deixa de ser free)
  useEffect(() => {
    if (periodType !== 'free') {
      setLocalStartDate(null);
      setLocalEndDate(null);
    }
  }, [periodType]);

  useEffect(() => {
    if (!analysisPeriod?.start || !analysisPeriod?.end) return;
    const start = parse(analysisPeriod.start, 'yyyy-MM-dd', new Date());
    const end = parse(analysisPeriod.end, 'yyyy-MM-dd', new Date());
    setLocalStartDate(start);
    setLocalEndDate(end);
  }, [analysisPeriod]);

  const startDate = useMemo(() => localStartDate || (analysisPeriod?.start ? parse(analysisPeriod.start, 'yyyy-MM-dd', new Date()) : null), [localStartDate, analysisPeriod]);

  const endDate = useMemo(() => localEndDate || (analysisPeriod?.end
    && analysisPeriod.start !== analysisPeriod.end
    ? parse(analysisPeriod.end, 'yyyy-MM-dd', new Date())
    : null), [localEndDate, analysisPeriod]);

  const minDateObj = useMemo(() => {
    if (!validityDates.length) return undefined;
    const sorted = [...validityDates].sort();
    return parse(sorted[0], 'yyyy-MM-dd', new Date());
  }, [validityDates]);

  const maxDateObj = useMemo(() => {
    if (!validityDates.length) return undefined;
    const sorted = [...validityDates].sort();
    return parse(sorted[sorted.length - 1], 'yyyy-MM-dd', new Date());
  }, [validityDates]);

  const handleDateChange = (dates: (Date | null)[] | Date | null) => {
    if (!Array.isArray(dates)) {
      setLocalStartDate(null);
      setLocalEndDate(null);
      setAnalysisPeriod(null, null);
      return;
    }
    const [newStartDate, newEndDate] = dates;
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);

    if (newStartDate && newEndDate) {
      setAnalysisPeriod(newStartDate, newEndDate);
    } else {
      setAnalysisPeriod(null, null);
    }
  };

  if (periodType !== 'free') {
    return null;
  }

  return (
    <Accordion.Item eventKey="analysis-period" id="analysis-period-accordion">
      <Accordion.Header>
        <TitleSemiBold>Período de análise</TitleSemiBold>
      </Accordion.Header>
      <Accordion.Body>
        {!hasSelectedRounds && (
          <div className="text-muted text-center py-3">
            Selecione as rodadas primeiro para carregar o período
          </div>
        )}
        {hasSelectedRounds && validityLoading && (
          <div className="text-muted text-center py-3">
            Carregando datas de validade…
          </div>
        )}
        {hasSelectedRounds && validityErrorMessage && (
          <div className="text-muted text-center py-3">
            {validityErrorMessage}
          </div>
        )}
        {hasSelectedRounds && !validityLoading && !validityErrorMessage && (
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            minDate={minDateObj}
            maxDate={maxDateObj}
            inline
          />
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}
